import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Segment, Header, Icon, Form, Button, Message, Divider, Label, Input
} from 'semantic-ui-react';
import FileDropZone from './FileDropZone';
import SignedImage from './SignedImage';
import { toKebab } from './FileStagingList';

/**
 * AddSeriesBookForm — the all-in-one "Add a Book to the Series" form.
 *
 * One panel, one terminal action. Rendered in place of the Books list (it overrides it).
 * Every book carries its OWN author (name + photo), seeded from the previous book.
 *
 * Props:
 *   onSubmit(data)  — { bookTitle, pdfFile, pdfAlias, coverFile, coverAlias, authorName, newAuthorFile }
 *                     may return a promise (button stays busy until it settles)
 *   onCancel        — return to the list without adding
 *   saving          — boolean (import in flight, from the parent)
 *   defaultAuthorName        — seed for the author name (previous book's author)
 *   currentAuthorImageS3Path — the author image this book would inherit (for preview)
 *   userId          — for signing the preview image
 *   submitLabel     — terminal button label (default "Add Book to Series")
 */
const FIELD_W = 300;   // title / author name width
const DROP_H = 150;    // PDF / Cover drop square height
const PHOTO = 120;     // author photo box
const SECTION = { borderLeft: '4px solid #2185d0', paddingLeft: '1em', marginBottom: '0.5em', borderRadius: ".28571429rem" };

const AddSeriesBookForm = ({
  onSubmit,
  onCancel,
  saving = false,
  defaultAuthorName = '',
  currentAuthorImageS3Path = null,
  userId,
  submitLabel = 'Add Book to Series',
}) => {
  const [bookTitle, setBookTitle] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [authorName, setAuthorName] = useState(defaultAuthorName || '');
  const [newAuthorFile, setNewAuthorFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const authorInputRef = useRef(null);

  // Seed author name from the previous book while the field is still empty.
  useEffect(() => {
    setAuthorName((prev) => (prev ? prev : (defaultAuthorName || '')));
  }, [defaultAuthorName]);

  // Local object-URL preview for a freshly picked author photo (overrides the current).
  useEffect(() => {
    if (!newAuthorFile) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(newAuthorFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [newAuthorFile]);

  const busy = saving || submitting;

  const kebab = bookTitle ? toKebab(bookTitle) : '';
  const pdfAlias = kebab ? `${kebab}.pdf` : (pdfFile ? pdfFile.name : '');
  const coverAlias = useMemo(() => {
    if (!coverFile) return '';
    const ext = '.' + (coverFile.name.split('.').pop() || 'jpg');
    return kebab ? `${kebab}${ext}` : coverFile.name;
  }, [coverFile, kebab]);

  // Required: title + pdf + cover. Author name defaults from the prior book.
  const complete = !!(bookTitle.trim() && pdfFile && coverFile);

  const handleSubmit = async () => {
    if (!complete) {
      setMessage({ type: 'error', text: 'Add a title, a PDF, and a cover to continue.' });
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        bookTitle: bookTitle.trim(),
        pdfFile,
        pdfAlias,
        coverFile,
        coverAlias,
        authorName: authorName.trim() || null,
        newAuthorFile,
      });
    } finally {
      setSubmitting(false); // parent unmounts on success; re-enables on error
    }
  };

  const pickAuthorPhoto = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) setNewAuthorFile(f);
    e.target.value = '';
  };

  const check = (ok) => (
    <Icon name={ok ? 'check circle' : 'circle outline'} color={ok ? 'green' : 'grey'} />
  );

  return (
    <Segment>
      <Header as="h3">
        <Icon name="book" />
        <Header.Content>
          Add a Book to the Series
          <Header.Subheader>One panel — fill it in, then add. Nothing runs until you click Add.</Header.Subheader>
        </Header.Content>
      </Header>

      <Form>
        {/* ① The Book */}
        <div style={SECTION}>
          <Header as="h4" style={{ marginBottom: '0.6em' }}>
            <Label circular color="blue" size="small">1</Label> The Book
          </Header>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75em' }}>
            <label style={{ minWidth: 80, textAlign: 'right', fontWeight: 'bold', margin: 0 }}>Book Title</label>
            <Input
              placeholder="Enter the book title…"
              value={bookTitle}
              onChange={(e) => setBookTitle(e.target.value)}
              disabled={busy}
              style={{ width: FIELD_W }}
            />
          </div>

          <div style={{ display: 'flex', gap: '1em', marginTop: '0.75em', alignItems: 'stretch' }}>
            <div style={{ flex: 1 }}>
              {pdfFile ? (
                <Segment secondary style={{ margin: 0, height: DROP_H, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <div><Icon name="file pdf outline" color="red" /> <strong>{pdfFile.name}</strong></div>
                  <div style={{ fontSize: '0.75em', color: '#888', margin: '0.3em 0' }}>saves as {pdfAlias}</div>
                  <Button icon="close" basic color="red" size="mini" content="Remove"
                    onClick={() => setPdfFile(null)} disabled={busy} />
                </Segment>
              ) : (
                <FileDropZone
                  accept=".pdf" multiple={false} compact heightPx={DROP_H}
                  label="PDF (required)"
                  icon="file pdf outline" maxSizeMB={100}
                  disabled={busy || !bookTitle.trim()}
                  onFilesSelected={(files) => setPdfFile(files[0])}
                />
              )}
            </div>

            <div style={{ flex: 1 }}>
              {coverFile ? (
                <Segment secondary style={{ margin: 0, height: DROP_H, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <div><Icon name="image" color="blue" /> <strong>{coverFile.name}</strong></div>
                  <div style={{ fontSize: '0.75em', color: '#888', margin: '0.3em 0' }}>saves as {coverAlias}</div>
                  <Button icon="close" basic color="red" size="mini" content="Remove"
                    onClick={() => setCoverFile(null)} disabled={busy} />
                </Segment>
              ) : (
                <FileDropZone
                  accept=".jpg,.jpeg,.png,.webp" multiple={false} compact heightPx={DROP_H}
                  label="Cover (required)"
                  icon="image" maxSizeMB={20}
                  disabled={busy || !bookTitle.trim()}
                  onFilesSelected={(files) => setCoverFile(files[0])}
                />
              )}
            </div>
          </div>
        </div>

        <Divider />

        {/* ② This book's Author */}
        <div style={SECTION}>
          <Header as="h4" style={{ marginBottom: '0.6em' }}>
            <Label circular color="blue" size="small">2</Label> This Book's Author
          </Header>

          <div style={{ display: 'flex', gap: '1.5em', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* Single click-to-replace photo — shows the new photo if picked, else the current */}
            <div>
              <div
                onClick={() => !busy && authorInputRef.current && authorInputRef.current.click()}
                title="Click to choose a photo for this book"
                style={{
                  width: PHOTO, height: PHOTO, borderRadius: 8, overflow: 'hidden',
                  position: 'relative', cursor: busy ? 'default' : 'pointer',
                  border: '2px dashed #ccc', background: '#fafafa',
                }}
              >
                {previewUrl ? (
                  <img src={previewUrl} alt="new author" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <SignedImage
                    s3Path={currentAuthorImageS3Path}
                    userId={userId}
                    alt={authorName || 'Author'}
                    width={`${PHOTO}px`} height={`${PHOTO}px`}
                    fallbackIcon="user circle"
                  />
                )}
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  background: 'rgba(0,0,0,0.55)', color: '#fff',
                  fontSize: '0.68em', textAlign: 'center', padding: '2px 0',
                }}>
                  {previewUrl ? 'New photo · click to change' : 'Click to change'}
                </div>
              </div>
              {previewUrl && (
                <Button basic size="mini" content="Revert" icon="undo"
                  style={{ marginTop: '0.4em' }} disabled={busy}
                  onClick={() => setNewAuthorFile(null)} />
              )}
              <input ref={authorInputRef} type="file" accept=".jpg,.jpeg,.png,.webp"
                style={{ display: 'none' }} onChange={pickAuthorPhoto} />
            </div>

            <div style={{ flex: '1 1 260px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75em' }}>
                <label style={{ minWidth: 80, textAlign: 'right', fontWeight: 'bold', margin: 0 }}>Author</label>
                <Input
                  placeholder="Author for this book"
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                  disabled={busy}
                  style={{ width: FIELD_W }}
                />
              </div>
              <div style={{ fontSize: '0.8em', color: '#888', marginTop: '0.4em', marginLeft: 'calc(80px + 0.75em)' }}>
                Defaults to the previous book's author — change it if a different author
                wrote this one. Click the photo to give this book its own author image.
              </div>
            </div>
          </div>
        </div>
      </Form>

      {message && (
        <Message negative={message.type === 'error'} size="small" style={{ marginTop: '1em' }}
          onDismiss={() => setMessage(null)}>
          <Icon name="warning sign" /> {message.text}
        </Message>
      )}

      {/* Completion line + terminal actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1em', flexWrap: 'wrap', gap: '0.5em' }}>
        <div style={{ fontSize: '0.9em', color: '#555' }}>
          Needed: {check(!!bookTitle.trim())} Title&nbsp;&nbsp;{check(!!pdfFile)} PDF&nbsp;&nbsp;{check(!!coverFile)} Cover&nbsp;&nbsp;{check(!!authorName.trim())} Author
        </div>
        <div style={{ display: 'flex', gap: '0.5em' }}>
          <Button onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button primary onClick={handleSubmit} disabled={!complete || busy} loading={busy}>
            <Icon name="plus" /> {submitLabel}
          </Button>
        </div>
      </div>
    </Segment>
  );
};

export default AddSeriesBookForm;
